// Global typing animation state management
interface TypingState {
  isAnimating: boolean;
  currentWordCount: number;
  totalWords: number;
  displayMessage: string;
  intervalId?: NodeJS.Timeout;
}

const typingAnimations = new Map<string, TypingState>();

// Helper functions for managing typing animations
export const startTypingAnimation = (
  commentId: string,
  fullMessage: string,
  onUpdate: (message: string) => void,
  onComplete: () => void
) => {
  const words = fullMessage.split(' ');
  const startingWords = Math.round(Math.random() * 6 + 4);

  // Clear any existing animation for this comment
  const existing = typingAnimations.get(commentId);
  if (existing?.intervalId) {
    clearInterval(existing.intervalId);
  }

  let currentWordCount = Math.min(startingWords, words.length);
  const displayMessage = words.slice(0, currentWordCount).join(' ');

  typingAnimations.set(commentId, {
    isAnimating: true,
    currentWordCount,
    totalWords: words.length,
    displayMessage,
  });

  onUpdate(displayMessage);

  const animate = () => {
    const state = typingAnimations.get(commentId);
    if (!state || !state.isAnimating) return;

    if (state.currentWordCount < state.totalWords) {
      const wordsToAdd = Math.round(Math.random() * 6 + 4);
      const newWordCount = Math.min(state.currentWordCount + wordsToAdd, state.totalWords);
      const newMessage = words.slice(0, newWordCount).join(' ');

      state.currentWordCount = newWordCount;
      state.displayMessage = newMessage;

      onUpdate(newMessage);
    } else {
      // Animation complete
      state.isAnimating = false;
      if (state.intervalId) {
        clearInterval(state.intervalId);
      }
      onComplete();
    }
  };

  const intervalId = setInterval(animate, 150);
  const state = typingAnimations.get(commentId)!;
  state.intervalId = intervalId;
};

export const getTypingState = (commentId: string): TypingState | undefined => {
  return typingAnimations.get(commentId);
};

export const clearTypingAnimation = (commentId: string) => {
  const state = typingAnimations.get(commentId);
  if (state?.intervalId) {
    clearInterval(state.intervalId);
  }
  typingAnimations.delete(commentId);
};

export const isTypingComplete = (commentId: string): boolean => {
  const state = typingAnimations.get(commentId);
  return state ? !state.isAnimating : false;
};

export const getCurrentTypingMessage = (commentId: string): string | undefined => {
  const state = typingAnimations.get(commentId);
  return state?.displayMessage;
};
